import { Fragment, useCallback, useEffect, useReducer, useRef, useState } from "react";
import { axiosRequestData } from "../../helpers/axios";
import parse from "html-react-parser";
import { Box, Button, Grid, useMediaQuery, useTheme } from "@mui/material";
import { Typography } from "@mui/material";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import StarRateRoundedIcon from "@mui/icons-material/StarRateRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import React from "react";
import { Course, CourseType } from "../../interfaces/course";
import { Lesson } from "../../interfaces/lesson";
import styles from "./coursePlaylist.module.css";
import { useNavigate } from "react-router";
import { removeDomainFromUrl, getTimeFromSeconds, replaceCourseSlug } from "../../helpers/utils";
import { CurrentLesson } from "../../interfaces/currentLesson";
import {
   Accordion,
   AccordionDetails,
   AccordionSummary,
} from "./accordionStyles";
import {
   lessonCompleteEvent,
   favoriteVideoEvent,
   clickVipProductRecommendation,
   clickVipLearnMore,
} from "../mixPanelEvents/mixPanelEvents";
import { addToNavigationFavoriteCount } from "../../helpers/document";
import { Accelerator } from "../../interfaces/accelerator";
import { useCoachingBannerVisibility } from "../../hooks/useCoachingBannerVisibility";
import ChampionsPassBanner from "../banner/chapmionsPassBanner/championsPassBanner";
import { CollectionsOutlined, Height } from "@mui/icons-material";
import Scrollbar from "react-scrollbars-custom";
import CheckAnimation from "../icons/animatedCheck";

function reducer(
   lessonPlaylist: Lesson[],
   action: { type: string; lessonID: number; lessons: Lesson[] }
) {
   switch (action.type) {
      case "set": {
         return action.lessons;
      }
      case "toggle_favorite": {
         return lessonPlaylist.map((t) => {
            if (t.ID === action.lessonID) {
               return { ...t, is_fav: !t.is_fav };
            } else {
               return t;
            }
         });
      }
      case "complete_lesson": {
         return lessonPlaylist.map((t) => {
            if (t.ID === action.lessonID) {
               return { ...t, complete: true };
            } else {
               return t;
            }
         });
      }
      default: {
         Error("Unknown action: " + action.type);
      }
   }
   throw Error("Unknown action: " + action.type);
}

const CoursePlaylist = (
    {
        setPreventAutoScroll,
        tmpHeaderHeight,
        courseHeaderRef,
        ctaRef,
        totalVideoPlayerHeight,
        course,
        currentLesson,
        lessonComplete,
        setShowModal,
        setAcceleratorProgress,
        expanded,
        setExpanded,
        scrollbarsRef,
        playerRef,
        playerHeight,
        videoLoaded,
        accelerator,
        moduleIndex,
        handleIsShowChampionsPassModal
    }: {
        setPreventAutoScroll: (params: boolean) => void,
        tmpHeaderHeight: number,
        courseHeaderRef: HTMLDivElement | null,
        ctaRef: React.RefObject<HTMLButtonElement>,
        totalVideoPlayerHeight: number,
        course: Course,
        currentLesson: CurrentLesson,
        lessonComplete: {
            lessonID: number,
            courseID: number,
            wasComplete: boolean
        },
        setShowModal: (params: boolean) => void,
        setAcceleratorProgress: (params: any) => void,
        expanded: number | boolean,
        setExpanded: React.Dispatch<React.SetStateAction<number | boolean>>,
        scrollbarsRef: Scrollbar | null,
        playerRef: null | HTMLDivElement,
        playerHeight: number,
        videoLoaded: boolean,
        accelerator:Accelerator,
        moduleIndex:number,
        handleIsShowChampionsPassModal:(value:boolean)=>void
    }
) => {

   // @ts-ignore
   window.realLesonNumber = 0;

   const [lessonPlaylist, dispatch] = useReducer(reducer, course.lessons);
   const navigate = useNavigate();
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('md'))
   const accordionRef = useRef<null | HTMLDivElement>(null);
   const myRef = useRef<null | HTMLDivElement>(null);
   const hasChamp = window.pgUser?.products?.some((product: string) => product === '125' || product === '124')
   const [isSharingThingOpened, setIsSharingThingOpened] = useState<Record<number, boolean>>({});
   const [lessonsThing, setLessonsThing] = useState<any>(lessonPlaylist);
   const [firstTimeComplete, setFirstTimeComplete] = useState(0);
   const [accordionCompletedLessons, setAccordionCompletedLessons] = useState<number[]>([]);
   const [accordionFirstTimeComplete, setAccordionFirstTimeComplete] = useState(false);
   const [coursesCompleted, setCoursesCompleted] = useState<number[]>([]);
   const [tmpClickedLesson, setTmpClickedLesson] = useState<Lesson[]>([]);
   const [tmpClonedNodeHeights, setTmpClonedNodeHeights] = useState<number[]>([]);
   const lastHeightRef = useRef<number | null | undefined>(null);
   const [openAccordions, setOpenAccordions] = useState<number[]>([course.ID]);

   useEffect(() => {
      setLessonsThing(lessonPlaylist);
   }, [lessonPlaylist]);
   
   useEffect(() => {
      if (
         lessonComplete.courseID === course.ID &&
         !lessonComplete.wasComplete
      ) {
         setFirstTimeComplete(lessonComplete.lessonID);
         const newLessonId = lessonComplete.lessonID;
         setAccordionCompletedLessons(prev =>
            prev.includes(newLessonId) ? prev : [...prev, newLessonId]
         );
         const filteredLessonIds = course.lessons
            .filter(
               (lesson) =>
                  !accordionCompletedLessons.includes(lesson.ID) &&
                  lesson.ID !== newLessonId &&
                  !lesson.complete &&
                  lesson.lesson_type === 'video'
            )
            .map((lesson) => lesson.ID);
         if(filteredLessonIds.length === 0) {
            setAccordionFirstTimeComplete(true);
            setCoursesCompleted(prev => ({
               ...prev,
               [course.ID]: course.ID
             }));
         }
         axiosRequestData(
            "POST",
            "/wp-json/pg/v2/lesson/" + lessonComplete.lessonID + "/complete",
            { courseID: accelerator.ID }
         ).then((response) => {
            setAcceleratorProgress(
               response.data.data.progress
            );
            lessonCompleteEvent(
               lessonComplete,
               lessonPlaylist,
               course,
               accelerator
            );
            dispatch({
               type: "complete_lesson",
               lessonID: lessonComplete.lessonID,
               lessons: course.lessons,
            });
         });
      }
      // eslint-disable-next-line
   }, [lessonComplete]);

   useEffect(() => {
      // on lesson change stop prevous check animation
      setFirstTimeComplete(0);
      setAccordionFirstTimeComplete(false);
   }, [currentLesson]);

   useEffect(() => {
      dispatch({ type: "set", lessons: course.lessons, lessonID: 0 });
      setExpanded(currentLesson.courseID);

      // if (accelerator.collapsable_accordions) {
      //    // open the accordion of the current lesson
      //    setOpenAccordions([currentLesson.courseID]);
      // } else {
      //    setOpenAccordions((prev) =>
      //       prev.includes(course.ID) ? prev.filter((item) => item !== course.ID) : [...prev, course.ID]
      //    );
      // }
   }, [course]);

   useEffect(() => {
      if (currentLesson?.courseID) {
         if (accelerator.collapsable_accordions) {
            // multiple open allowed → add if not already there
            setOpenAccordions(prev =>
               prev.includes(currentLesson.courseID)
                  ? prev
                  : [...prev, currentLesson.courseID]
            );
         } else {
            // only one open at a time
            setOpenAccordions([currentLesson.courseID]);
         }
      }
   }, [currentLesson, accelerator.collapsable_accordions]);

   const handleAccordionChange = (courseID: number) => {
      if (accelerator.collapsable_accordions) {
         // toggle on/off
         setOpenAccordions(prev =>
            prev.includes(courseID)
               ? prev.filter(id => id !== courseID)
               : [...prev, courseID]
         );
      } else {
         // only one accordion at a time
         setOpenAccordions(prev =>
            prev.includes(courseID) ? [] : [courseID]
         );
      }
   };



   // handle click on accordion if collapsable_accordions is true
   // const handleAccordionChange = (id: number) => {
   //    if (!accelerator.collapsable_accordions) return;
   //    setOpenAccordions((prev) =>
   //       prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
   //    );
   // };

   // useEffect(() => {
   //    if (
   //       isMobile &&
   //       expanded === course.ID &&
   //       currentLesson.courseID === course.ID &&
   //       accordionRef.current
   //    ) {
   //       accordionRef.current.scrollIntoView({
   //          behavior: "auto",
   //          block: "start",
   //       });
   //    }
   //    // eslint-disable-next-line
   // }, [expanded, course]);

   useEffect(() => {
      if (
         isMobile &&
         expanded === course.ID &&
         currentLesson.courseID === course.ID &&
         accordionRef.current &&
         !course.lessons.some((lesson) => lesson.lesson_type === "cta" && currentLesson.lesson_type !== "text")
      ) {
         // accordionRef.current.scrollIntoView({
         //    behavior: "auto",
         //    block: "start",
         // });
         console.log("scroll line 111");
         console.log("course is >> ", course);
         console.log("accordionref >> ", accordionRef);
      }
      // eslint-disable-next-line
   }, [expanded, course]);
   //  useEffect(() => {
   //      if (isMobile && expanded === course.ID && currentLesson.courseID === course.ID && accordionRef.current && !course.lessons.some((lesson) => lesson.lesson_type === "cta") ) {
   //          accordionRef.current.scrollIntoView({
   //              behavior: "auto",
   //              block: "start"
   //          })
   //          // console.log("scroll line 111")
   //          // console.log("course is >> ", course)
   //          // console.log("accordionref >> ", accordionRef)
   //      }
   //      // eslint-disable-next-line
   //  }, [expanded, course]);
   useEffect(() => {
      if (currentLesson && currentLesson.lesson_type !== "text" && currentLesson.lesson_type !== "link" && isMobile) {
         setTmpClickedLesson([currentLesson]);
      }
   }, [currentLesson, isMobile]);

   function createClonedNodeCtaLessonBanner(lesson: Lesson): Promise<number> {
      return new Promise((resolve) => {
         if (lesson.cta && lesson.cta.text) {
            const buttonEle = document.createElement("button");
            buttonEle.style.background = "#3e59d9";
            buttonEle.style.paddingTop = "18px";
            buttonEle.style.paddingBottom = "18px";
            buttonEle.style.display = "inline-flex";
   
            const firstSpanEle = document.createElement("span");
            firstSpanEle.innerHTML = `Learn More About a <span class="fw-bold">VIP Experience</span>`;
            firstSpanEle.style.fontSize = "15px";
            firstSpanEle.style.lineHeight = "18px";
            firstSpanEle.style.fontWeight = "400";
   
            const secondSpanEle = document.createElement("span");
            secondSpanEle.textContent = "Click Here";
            secondSpanEle.style.fontSize = "15px";
            secondSpanEle.style.lineHeight = "18px";
            secondSpanEle.style.fontWeight = "400";
            secondSpanEle.style.textDecoration = "underline";
            secondSpanEle.style.marginLeft = "8px";
   
            firstSpanEle.style.color = "white";
            secondSpanEle.style.color = "white";
   
            buttonEle.style.width = "calc(100% - 40px)";
            buttonEle.style.marginLeft = "20px";
            buttonEle.style.marginRight = "20px";
            buttonEle.style.justifyContent = "center";
            buttonEle.id = "proxy-element4";
            buttonEle.style.opacity = "0";
   
            buttonEle.appendChild(firstSpanEle);
            buttonEle.appendChild(secondSpanEle);
            document.body.appendChild(buttonEle);
   
            setTimeout(() => {
               resolve(buttonEle.offsetHeight);
            }, 500);
         }
      });
   }

   function createClonedNodeCurrentCtaBanner(lesson: Lesson): Promise<number> {
      return new Promise((resolve) => {
         if (lesson.cta && lesson.cta.text) {
            const buttonEle = document.createElement("button");
            buttonEle.style.background = "#3e59d9";
            buttonEle.style.paddingTop = "18px";
            buttonEle.style.paddingBottom = "18px";
            buttonEle.style.display = "inline-flex";
   
            const firstSpanEle = document.createElement("span");
            firstSpanEle.innerHTML = lesson.cta.text;
            firstSpanEle.style.fontSize = "15px";
            firstSpanEle.style.lineHeight = "18px";
            firstSpanEle.style.fontWeight = "400";
   
            const secondSpanEle = document.createElement("span");
            secondSpanEle.textContent = "Click Here";
            secondSpanEle.style.fontSize = "15px";
            secondSpanEle.style.lineHeight = "18px";
            secondSpanEle.style.fontWeight = "400";
            secondSpanEle.style.textDecoration = "underline";
            secondSpanEle.style.marginLeft = "8px";
   
            firstSpanEle.style.color = "white";
            secondSpanEle.style.color = "white";
   
            buttonEle.style.width = "calc(100% - 40px)";
            buttonEle.style.marginLeft = "20px";
            buttonEle.style.marginRight = "20px";
            buttonEle.style.justifyContent = "center";
            buttonEle.id = "proxy-element3";
            buttonEle.style.opacity = "0";
   
            buttonEle.appendChild(firstSpanEle);
            buttonEle.appendChild(secondSpanEle);
            document.body.appendChild(buttonEle);
   
            setTimeout(() => {
               resolve(buttonEle.offsetHeight);
            }, 500);
         }
      });
   }

   function createClonedNodeCtaBanner(lesson: Lesson): Promise<number> {
      return new Promise((resolve) => {
         const buttonEle = document.createElement("button");
         buttonEle.style.background = "#3e59d9";
         buttonEle.style.paddingTop = "18px";
         buttonEle.style.paddingBottom = "18px";
         buttonEle.style.display = "inline-flex";

         const firstSpanEle = document.createElement("span");
         firstSpanEle.innerHTML = accelerator.ctaTextForAllLessons;
         firstSpanEle.style.fontSize = "15px";
         firstSpanEle.style.lineHeight = "18px";
         firstSpanEle.style.fontWeight = "400";

         const secondSpanEle = document.createElement("span");
         secondSpanEle.textContent = "Click Here";
         secondSpanEle.style.fontSize = "15px";
         secondSpanEle.style.lineHeight = "18px";
         secondSpanEle.style.fontWeight = "400";
         secondSpanEle.style.textDecoration = "underline";
         secondSpanEle.style.marginLeft = "8px";

         firstSpanEle.style.color = "white";
         secondSpanEle.style.color = "white";

         buttonEle.style.width = "calc(100% - 40px)";
         buttonEle.style.marginLeft = "20px";
         buttonEle.style.marginRight = "20px";
         buttonEle.style.justifyContent = "center";
         buttonEle.id = "proxy-element2";

         buttonEle.appendChild(firstSpanEle);
         buttonEle.appendChild(secondSpanEle);
         document.body.appendChild(buttonEle);

         setTimeout(() => {
            resolve(buttonEle.offsetHeight);
         }, 500);
      });
   }

   function createClonedNodeHeader(lesson: Lesson): Promise<number> {
      return new Promise((resolve) => {
         const divEle = document.createElement("div");
         const divImg = document.createElement("img");
         const divParagraph = document.createElement("p");

         divImg.src = "https://www.performancegolf.com/wp-content/uploads/2023/11/Video-Icon.png";
         divImg.style.height = "100%";
         divImg.style.marginRight = "7px";

         const divTextContent = lesson.name + " (00:00)";
         divParagraph.textContent = divTextContent;
         divParagraph.style.marginBottom = "0";
         divParagraph.style.fontSize = "14px";
         divParagraph.style.color = "#fff";

         divEle.appendChild(divImg);
         divEle.appendChild(divParagraph);
         divEle.style.padding = "8px";
         divEle.style.marginLeft = "20px";
         divEle.style.marginRight = "20px";
         divEle.style.background = "#000";
         divEle.style.display = "flex";

         divEle.id = "proxy-element";

         document.body.appendChild(divEle);

         setTimeout(() => {
            resolve(divEle.offsetHeight);
         }, 500);
      });
   }

   useEffect(() => {
      if (currentLesson && currentLesson.lesson_type !== "text" && currentLesson.lesson_type !== "link" && isMobile) {
         setTmpClonedNodeHeights([tmpHeaderHeight]);
      }
   }, [currentLesson]);


   const playLesson = async (lesson: Lesson) => {
      setPreventAutoScroll(true);
      setShowModal(false);
      if (lesson.lesson_type === 'link') {
         window.open(lesson.link.url) 
      } else if ((lesson.video.ID !== currentLesson.video.ID)? true : true) {
         if(lesson.lesson_type === 'cta'){
            const regexp = /utm_campaign=.*banner_2/;
            let value= regexp.test(lesson.cta?.url);
            let location = value? "Playlist End of Course" : "Playlist Module 1";
            clickVipLearnMore(location, lesson, accelerator);
         }
         if (lesson.custom_module_lesson) {
            let newUrl = replaceCourseSlug(lesson.url, accelerator.name);
            navigate(newUrl);
         } else {
            navigate(removeDomainFromUrl(lesson.url));
         }
      }

      if (lesson.lesson_type !== "text" && lesson.lesson_type !== "link" && isMobile) {
         setTmpClickedLesson((prevLesson) => [...prevLesson, lesson]);

         function getVimeoPlayerSize(): [number, number] {
            const width: number = window.innerWidth - 40;
            const height: number = width * 9 / 16;
            
            return [width, height];
         }
         
         let bannerHeight = courseHeaderRef?.clientHeight ?? 109;
         const [_, playerHeight] = getVimeoPlayerSize();
         
         const playingLessonId = lesson.ID;
         
         if (lesson.lesson_type === "cta") {
            console.log("a toto ked je cta");
            setTimeout(async () => {
               const regexp = /utm_campaign=.*banner_2/;
               const value = regexp.test(lesson.cta?.url);
               const playingLessonElements = document.querySelectorAll(`#lesson-id-${playingLessonId}`);
               const playingLessonElement = value ? playingLessonElements[1] : playingLessonElements[0];
               const clonedCurrentCtaBannerNodeHeight = lesson.cta ? await createClonedNodeCtaLessonBanner(lesson) : 0;

               if (playingLessonElement) {
                  const elementTop = playingLessonElement.getBoundingClientRect().top + window.scrollY;
                  const offset = bannerHeight + playerHeight + (Number(sessionStorage.getItem("lastHeight")) ?? 0) + clonedCurrentCtaBannerNodeHeight + 20;

                  window.scrollTo({ top: window.scrollY + 10 });
                  
                  setTimeout(() => {
                     window.scrollTo({
                        top: elementTop - offset,
                        behavior: "smooth",
                     });
                  }, 100);
               }
            }, 200);
         } 
         if (lesson.lesson_type !== "cta") {
            const playingLessonElement = document.querySelector(`#lesson-id-${playingLessonId}`);
            await new Promise<void>(resolve => setTimeout(resolve, 500));
            const newBanner = document.getElementById(`lesson-banner-${lesson.ID}`);
            let titleHeight = tmpHeaderHeight || 40;
            if (newBanner) {
               const savedDisplay = newBanner.style.display;
               newBanner.style.display = 'block';
               titleHeight = newBanner.offsetHeight || titleHeight;
               newBanner.style.display = savedDisplay;
            }
            const clonedCtaBannerNodeHeight = await createClonedNodeCtaBanner(lesson);
            const clonedCurrentCtaBannerNodeHeight = lesson.cta && !accelerator.showVipBannerToAllLessons ? await createClonedNodeCurrentCtaBanner(lesson) : 0;
            sessionStorage.setItem("lastHeight", titleHeight.toString());

            setTmpClonedNodeHeights((prevHeights) => {
               const newHeights = [tmpHeaderHeight, titleHeight, ...prevHeights];
               lastHeightRef.current = newHeights.at(-1);
               return newHeights;
            });

            if (playingLessonElement) {
               console.log("volaco damir");
               const elementTop = playingLessonElement.getBoundingClientRect().top + window.scrollY;
               const offset = bannerHeight + titleHeight + playerHeight + (accelerator.showVipBannerToAllLessons ? clonedCtaBannerNodeHeight : 0) + (lesson.cta ? clonedCurrentCtaBannerNodeHeight : 0);

               window.scrollTo({ top: window.scrollY + 10 });

               setTimeout(() => {
                  window.scrollTo({
                     top: elementTop - offset,
                     behavior: "smooth",
                  });
               }, 100);
            
               setTimeout(() => {
                  document.querySelector("#proxy-element")?.remove();
               }, 1000);

               setTimeout(() => {
                  document.querySelector("#proxy-element2")?.remove();
               }, 1000);

               setTimeout(() => {
                  document.querySelector("#proxy-element3")?.remove();
               }, 1000);

               setTimeout(() => {
                  document.querySelector("#proxy-element4")?.remove();
               }, 2000);
            }
            // }, 500);
         }

      }
        setShowModal(false);
        if (lesson.lesson_type === 'link') {
            window.open(lesson.link.url) 
        } else if ((lesson.video.ID !== currentLesson.video.ID)? true : true) {
            if(lesson.lesson_type === 'cta'){
               const regexp = /utm_campaign=.*banner_2/;
               let value= regexp.test(lesson.cta?.url);
               let location = value? "Playlist End of Course" : "Playlist Module 1";
               clickVipLearnMore(location, lesson, accelerator);
            }
            
            // Check if this is a playlist course and generate appropriate URL
            let navigationUrl = removeDomainFromUrl(lesson.url);
            
            if (accelerator.course_type === CourseType.Playlist) {
               // Extract lesson slug from original URL
               const lessonSlug = lesson.url.split('/').filter(Boolean).pop();
               // Extract current course slug from current URL
               const currentPath = window.location.pathname;
               const pathSegments = currentPath.split('/').filter(Boolean);
               
               if (pathSegments.length >= 2 && pathSegments[0] === 'courses') {
                  const playlistCourseSlug = pathSegments[1];
                  navigationUrl = `/courses/${playlistCourseSlug}/${lessonSlug}/`;
               }
            }
            
            if (lesson.custom_module_lesson) {
               return;
            }
            navigate(navigationUrl);
        }
    }

   const toggleFavorite = (lesson: Lesson, e: Event) => {
      console.log("e target", e.target)
      if (lesson.is_fav) {
         axiosRequestData(
            "DELETE",
            "/wp-json/pg/v2/lesson/" + lesson.ID + "/favorite",
            { courseID: accelerator.ID }
         ).then((response) => {
            dispatch({
               type: "toggle_favorite",
               lessonID: lesson.ID,
               lessons: course.lessons,
            });
            addToNavigationFavoriteCount(-1, isMobile);
         });
      } else {
         axiosRequestData(
            "POST",
            "/wp-json/pg/v2/lesson/" + lesson.ID + "/favorite",
            { courseID: accelerator.ID }
         ).then((response) => {
            dispatch({
               type: "toggle_favorite",
               lessonID: lesson.ID,
               lessons: course.lessons,
            });
            addToNavigationFavoriteCount(1, isMobile);
            // setAnimatedLessonId(lesson.ID);
            // setTimeout(() => setAnimatedLessonId(null), 300);
         });
      }
   };

   const setLessonNumberToZero = () => {
      // @ts-ignore
      window.realLesonNumber = 0;
   }

   const handleSharingThing = (lessonID: number) => {
      setIsSharingThingOpened((prevState) => ({
         ...prevState,
         [lessonID]: !prevState[lessonID], 
      }));
   }

   const toggleCompletion = (lesson: Lesson) => {
      setLessonsThing((prevLessons: any) => {
         return prevLessons.map((l: any) => {
            if (l.ID === lesson.ID) { 
               return { ...l, complete: !l.complete };  
            }
            return l;
         });
      });

      lessonsThing.forEach((lessonThing: Lesson) => {
         if (lessonThing.ID === lesson.ID) {
            if (lesson.complete) {
               handleMarkAsIncomplete(lesson);
            } else {
               handleMarkAsComplete(lesson);
            }
         }
      })
   };

   const handleMarkAsIncomplete = (lesson: Lesson) => {
      console.log("incomplete clicked");
      lesson.complete = false;
      axiosRequestData("POST", "/wp-json/pg/v2/lesson/" + lesson.ID + "/remove_complete",
            { courseID: accelerator.ID }).then((response) => {            
         setAcceleratorProgress(
            response.data.data.progress
         );
      });
   }

   const handleMarkAsComplete = (lesson: Lesson) => {
      console.log("complete clicked");
      lesson.complete = true;
      axiosRequestData("POST", "/wp-json/pg/v2/lesson/" + lesson.ID + "/complete",
            { courseID: accelerator.ID }).then((response) => {            
         setAcceleratorProgress(
            response.data.data.progress
         );
      });
   }

   const handleChange =
      (panel: number) =>
         (event: React.SyntheticEvent, newExpanded: boolean) => {
            // isMobile ? void (0) :
            setExpanded(newExpanded ? panel : false);
         };

   const scrollToNewAccordion = (panel: number) => 
      (event: React.SyntheticEvent, newExpanded: boolean) => {
      if (scrollbarsRef && !isMobile) {
         const screenWidth = window.innerWidth;
         const currentExpanded = accelerator.courses.findIndex((course) => course.ID === panel);

         let distance = 0;
         accelerator.courses.forEach((course, index) => {
            if (index < currentExpanded) {
               distance += document.getElementById(`accordion-header-${course.ID}`)?.clientHeight || 0;
            }
         });
         if (screenWidth <= 1024) {
            distance = distance + 35;
         }
         scrollbarsRef?.scrollTo(0, distance);
         if (currentExpanded >= 4) {
            smoothScrollTo(scrollbarsRef, 0, distance, 650);  
         }
         if (panel !== expanded) {
            setExpanded(newExpanded ? panel : false);
         } else {
            setExpanded(false);
         }
      }
   }

   const smoothScrollTo = (
      element: Scrollbar,
      targetX: number,
      targetY: number,
      duration: number = 500
    ) => {
      const startX = element.scrollLeft;
      const startY = element.scrollTop;
      const distanceX = targetX - startX;
      const distanceY = targetY - startY;
      const startTime = performance.now();
    
      const animateScroll = (currentTime: number) => {
        const elapsedTime = currentTime - startTime;
        const progress = Math.min(elapsedTime / duration, 1); // Ensure progress doesn't exceed 1
    
        // Ease-in-out function for smooth scrolling
        const easeInOutQuad = (t: number) =>
          t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    
        const easedProgress = easeInOutQuad(progress);
    
        element.scrollTo(
          startX + distanceX * easedProgress,
          startY + distanceY * easedProgress
        );
    
        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };
    
      requestAnimationFrame(animateScroll);
    };

   useEffect(() => {
      if (!isMobile) {
         const scrollToAccordionHeader = () => {
            if (isMobile && expanded === course.ID && myRef.current) {
               const accordionHeader = document.getElementById(`accordion-header-${course.ID}`);
   
               if (accordionHeader) {
                  console.log("inside line 170!")
                  // Scroll to the calculated position
                  myRef.current.scrollIntoView({
                     behavior: "auto",
                     block: "start"
                  })
               }
            }
         }
         scrollToAccordionHeader();
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [expanded, course]);

   const checkForUrlHash = (lesson: Lesson) => {
      let key = '#2/';
      return (window.location.hash != key && !lesson.url.endsWith(key) || (window.location.hash == key && lesson.url.endsWith(key)));
   };
    const renderPlaylistItem = (lesson: Lesson, lessonIndex: number,arr:Lesson[],moduleIndex:number) => {
         if (lesson.lesson_type === 'link' && lesson.link.url.endsWith(".pdf")) {
            lesson.linkIcon = '002-pdf-1.png';
         }

         if (lesson.lesson_type !== "cta") {
            // @ts-ignore
            window.realLesonNumber = parseInt(window.realLesonNumber) + 1;
         }
          // @ts-ignore
         const lessonNumber = window.realLesonNumber;
         const hasIntroduction = accelerator.courses[0].lessons[0].is_introduction === true

         const shouldShowBanner =   lessonIndex === 0 &&
                                    (
                                    (hasIntroduction && [1, 2].includes(moduleIndex)) ||
                                    (!hasIntroduction && [0, 1].includes(moduleIndex)) ||
                                    arr.some(el => el.is_pro_edition === true)
                                    ) &&
                                    !hasChamp &&
                                    accelerator.hasCpBanner &&
                                    !(arr.length === 1 && arr[0].lesson_type === 'cta');



         return <><Grid container key={lessonIndex} id={`lesson-id-${lesson.ID}`}
                     sx={{
                         borderTop: lessonIndex > 0 ? "1px solid hsla(0, 0%, 100%, 1.00)" : "none",
                         borderBottom: lessonIndex + 1 === lessonPlaylist.length ? "1px solid hsla(0, 0%, 100%, 1.00)" : "none",
                         backgroundColor: lesson.custom_module_lesson ? "rgba(92, 189, 129, 0.1)" : "#FCFAFA",
                     }}
                     className={
                         lesson.ID === currentLesson.ID && checkForUrlHash(lesson) ? 
                         styles.active :
                           (lesson.lesson_type === "cta") ? 
                           styles.goldVip : ''
                     }
                     >
                        {shouldShowBanner && (
                        <ChampionsPassBanner handleIsShowChampionsPassModal={handleIsShowChampionsPassModal}/>)}

                   <Grid item xs={lesson.lesson_type === 'video' ? 9.5 : 12} sm={lesson.lesson_type === 'video' ? 10 : 12}
                       className={styles.label} sx={{minHeight: lesson.lesson_type === "cta" ? {xs: "60px", sm:"77px"} : {xs: "44px", sm: "77px"}}}
                       onClick={() => {

                          if (lesson.lesson_type === 'cta') {
                             const regexp = /utm_campaign=.*banner_2/;
                             const value = regexp.test(lesson.cta?.url);
                             const location = value ? "Playlist End of Course" : "Playlist Module 1";
                             clickVipProductRecommendation(location, lesson, accelerator);
                             clickVipLearnMore(location, lesson, accelerator);
                             window.open(lesson.cta?.url, '_blank');
                          } else {
                           playLesson(lesson);
                          }
                       }}>
                        <Grid container height={"100%"}>
                            <Grid item xs={11} padding={0} display={"flex"}
                                margin={"auto"}>
                                {lesson.complete
                                    && firstTimeComplete !== lesson.ID
                                    && lesson.lesson_type !== "cta"
                                    && lesson.lesson_type !== "text" 
                                    &&  
                                 <Box
                                    component={"img"}
                                    sx={{
                                        width: { xs: "18px", md: "26px" },
                                        height: { xs: "18px", md: "26px" },
                                        margin: "auto 15px auto 0"
                                    }}
                                    src="https://members.performancegolf.com/wp-content/uploads/2025/02/Icon-1.svg"
                                    alt=""/>
                                    }
                                 {lesson.complete 
                                 && firstTimeComplete === lesson.ID 
                                 && !lessonComplete.wasComplete
                                 && lesson.lesson_type !== "cta"
                                 && lesson.lesson_type !== "text" 
                                 &&
                                    <Box
                                       sx={{
                                          position: "relative",
                                          width: { xs: "24px", md: "35px" },
                                          height: { xs: "24px", md: "35px" },
                                          margin: "auto 7px auto -4px",
                                          aspectRatio: "1 / 1",
                                       }}
                                       >
                                          <CheckAnimation/>
                                    </Box>
                                 } 
                                 {!lesson.complete && lesson.lesson_type === "link" && 
                                 <Box
                                       component={"img"}
                                       sx={{
                                          width: { xs: "18px", md: "26px" },
                                          height: { xs: "18px", md: "26px" },
                                          margin: "auto 15px auto 0"
                                       }}
                                       src={"https://members.performancegolf.com/wp-content/uploads/2024/04/" + (lesson.linkIcon ? lesson.linkIcon : "Vector.png") }
                                       alt=""/>}
                                 {lesson.lesson_type === "text" && 
                                 <Box
                                       component={"img"}
                                       sx={{
                                          width: { xs: "18px", md: "26px" },
                                          height: { xs: "18px", md: "26px" },
                                          margin: "auto 15px auto 0"
                                       }}
                                       src="https://members.performancegolf.com/wp-content/uploads/2024/04/001-text-1.png"
                                       alt=""/>}
                                {/* <Typography
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'flex-start',
                                        fontSize: "16px",
                                        lineHeight: "16px",
                                        margin: "auto 0"
                                    }}
                                > */}
                                <Typography
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: "center",
                                        fontSize: { xs: "12px", md: "16px" },
                                        lineHeight: { xs: "80%", md: "16px" },
                                        margin: "auto 0",
                                        paddingTop: {xs: "8px", md: "0"},
                                        paddingBottom: {xs: "8px", md: "0"},
                                    }}
                                >
                                    {/* {lesson.lesson_type !== 'cta' && <span style={{marginRight: '8px'}}>{lessonNumber}.</span>} */}
                                    {/* {lesson.lesson_type !== 'cta' && !lessonsThing.find((l: any) => l.ID === lesson.ID)?.complete && ( */}
                                    {lesson.lesson_type !== 'cta' && (!lesson.complete || !isMobile) && (
                                       <Typography
                                          component="span"
                                          sx={{ 
                                             fontSize: { xs: "14px", md: "16px" },
                                             marginRight: { xs: "12px", md: "8px" } 
                                          }} 
                                       >
                                          {lessonNumber}.
                                       </Typography>
                                    )}
                        
                                    {lesson.lesson_type === 'video' &&<span style={{
                                        flexGrow: 1,
                                        marginRight: '8px'
                                    }}>
                                       <Typography
                                          component="span"
                                          sx={{
                                             fontSize: "16px"
                                          }}
                                       >
                                       <Typography
                                          component="span"
                                          sx={{ 
                                             fontWeight: "400",
                                             fontSize: "16px"
                                          }} 
                                       >
                                          {parse(lesson.name)}
                                       </Typography>{" "}
                                       ({getTimeFromSeconds(Number(lesson.video.duration))})
                                    </Typography>
                                    </span>}

                                    {lesson.lesson_type !== 'video' && lesson.lesson_type !== 'cta' && <span style={{
                                        flexGrow: 1,
                                        marginRight: '8px'
                                    }}>{parse(lesson.name)}</span>}
                                    {lesson.lesson_type === 'cta' && 
                                    <Typography sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        fontSize: "16px",
                                        lineHeight: "16px",
                                        margin: "auto 0"
                                    }}>
                                        <span style={{flexGrow: 1,marginRight: '8px', fontSize:"14px", fontStyle:"italic"}}>{lesson.cta.text}</span>
                                        <span style={{flexGrow: 1,marginRight: '8px', fontSize:"14px"}}>{parse(lesson.name)}</span>
                                    </Typography>    
                                    }
                                </Typography>
                                {lesson.lesson_type === 'cta' &&
                                //slobodan adding regex to check if 1 or 2
                                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}> 
                                    <Button 
                                        sx={{display: 'flex',alignItems: 'center',}}
                                        onClick={(e: React.MouseEvent<HTMLElement>) => {
                                             e.stopPropagation();
                                             const regexp = /utm_campaign=.*banner_2/;   
                                             let value= regexp.test(lesson.cta?.url);
                                             let location = value? "Playlist End of Course" : "Playlist Module 1";
                                             clickVipProductRecommendation(location, lesson, accelerator);
                                             clickVipLearnMore(location, lesson, accelerator);
                                             // Only open survey; keep user on current lesson
                                             window.open(lesson.cta?.url, '_blank');
                                          }
                                       }
                                    // clickProduct(source === "player" ? "Video" : "Course Page", "Pop Up", "");
                                            className={styles.nextBtn} >
                                        <Typography sx={{fontWeight: 700, whiteSpace: "nowrap", flexGrow: 1, textAlign: 'center'}}>{window.pg?.l10n.learn_more}</Typography>
                                    </Button>
                                </div>
                                }
                            </Grid>
                        </Grid>
                    </Grid>
                    {lesson.lesson_type === 'video' &&
                        <Grid item className={styles.favStar} sx={{
                           flex: 1,
                           display: "flex",
                           alignItems: "center",
                           justifyContent: "space-around",
                        }}>
                           <Grid item className={styles.favStar}  sx={{ 
                              py: { xs: 0, md: 3 },
                           }} margin={"auto"}
                              textAlign={"center"}
                              onClick={(e: any) => {
                                 toggleFavorite(lesson, e)
                                 favoriteVideoEvent(lesson, course, !lesson.is_fav, accelerator)
                              }}>

                              {lesson.is_fav &&
                                 <StarRateRoundedIcon htmlColor="#FD3300"></StarRateRoundedIcon>}
                              {!lesson.is_fav && <StarBorderRoundedIcon></StarBorderRoundedIcon>}
                              
                           </Grid>
                        
                           {isMobile && (
                                 <Box
                                    component={"img"}
                                    sx={{
                                        width: { xs: "18px", md: "26px" },
                                        height: { xs: "18px", md: "26px" },
                                        margin: "auto 0 auto 8px"
                                    }}
                                    src="https://members.performancegolf.com/wp-content/uploads/2025/02/icon.svg"
                                    onClick={() => {
                                       handleSharingThing(lesson.ID);
                                    }}
                                    alt=""/>
                            )}
                           </Grid>
                        }
                </Grid>

               {isMobile && isSharingThingOpened[lesson.ID] && <Box sx={{
                  paddingLeft: "16px",
                  paddingTop: "8px",
                  paddingBottom: "14px",
               }}>
                  <Box>
                     <Box onClick={() => {
                        toggleCompletion(lesson)
                     }} sx={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "16px"
                     }}>
                        <Box
                           component="img"
                           sx={{marginRight: "16px"}}
                           src={
                              // lessonsThing.find((l: any) => l.ID === lesson.ID)?.complete || lesson.complete
                              lesson.complete
                              ? "https://members.performancegolf.com/wp-content/uploads/2025/02/Frame-143727386.svg"
                              : "https://members.performancegolf.com/wp-content/uploads/2025/02/Icon.svg"
                        }/>

                        <Typography sx={{ fontSize: "14px" }}>
                           {/* {lessonsThing.find((l: any) => l.ID === lesson.ID)?.complete || lesson.complete ? "Mark as Incomplete" : "Mark as Complete"} */}
                           {lesson.complete ? "Mark as Incomplete" : "Mark as Complete"}
                        </Typography> 

                     </Box>
                  </Box>

                  <Box sx={{
                     display: "flex",
                     alignItems: "center",
                     marginLeft: "2px"
                  }}
                     onClick={(e: any) => {
                        toggleFavorite(lesson, e)
                        favoriteVideoEvent(lesson, course, !lesson.is_fav, accelerator)
                     }}
                  >
                     {lesson.is_fav &&
                        <StarRateRoundedIcon htmlColor="#FD3300"></StarRateRoundedIcon>}
                     {!lesson.is_fav && <StarBorderRoundedIcon></StarBorderRoundedIcon>}

                     {lesson.is_fav &&
                        <Typography sx={{ fontSize: "14px", marginLeft: "18px"}}>Remove from Favorites</Typography>}
                     {!lesson.is_fav && <Typography sx={{ fontSize: "14px", marginLeft: "18px"}}>Add to Favorites</Typography>}
                     
                  </Box>
               </Box>}
            </>
    };

    const videoLessons = course.lessons.filter(lesson => lesson.lesson_type === 'video');
    const allLessonsComplete = videoLessons.length > 0 && videoLessons.every(lesson => lesson.complete);

   return (
      <>
         <Box ref={accordionRef} sx={{ scrollMarginTop: playerHeight }} onClick={() => setLessonNumberToZero()}>
            {!course.name && 
               lessonPlaylist.map((lesson, lessonIndex,arr) => (
                  <Fragment key={lesson.ID}>
                     {(lesson.is_introduction) && !course.lessons.some((lesson) => lesson.lesson_type === "cta") && (
                        <Box
                           key={lessonIndex}
                           sx={{ borderBottom: "1px solid hsla(0, 0%, 100%, 1.00)" }}
                        >
                           <Typography
                              onClick={() => playLesson(lesson)}
                              className={
                                 currentLesson.url === lesson.url
                                    ? styles.active
                                    : ""
                              }
                              sx={{
                                 fontSize: 16,
                                 margin: 0,
                                 fontWeight: 800,
                                 padding: { xs: "16px", md: "20px" }, 
                                 cursor: "pointer",
                              }}
                           >
                              {parse(lesson.name)}
                           </Typography>
                        </Box>
                     )}
                     {!lesson.is_introduction &&
                        renderPlaylistItem(lesson, lessonIndex,arr,moduleIndex)}
                  </Fragment>
               ))
            }

            {course.name && (
               <Box
                  className="row"
                  ref={myRef}
                  sx={{ scrollMarginTop: playerHeight + 44 + "px", position: "relative" }}
               >
                  {(currentLesson.lesson_type !== "text" || (currentLesson.lesson_type === "text" && !isMobile)) && <Accordion
                     // expanded={expanded === course.ID ? true : false}
                     // expanded={openAccordions.includes(course.ID)}
                     // onChange={() => handleAccordionChange(course.ID)}
                     expanded={accelerator.collapsable_accordions ? openAccordions.includes(course.ID) : true}
                     onChange={() => handleAccordionChange(course.ID)}
                  >
                     <Box
                        p={0}
                        m={0}
                        sx={{
                           position: { md: "inherit" },
                           zIndex: 1,
                           backgroundColor: "#FCFAFA",
                           //@ts-ignore
                           top: isMobile ? `${playerHeight}px` : 0,
                           borderBottom: "1px solid hsla(0, 0%, 100%, 1.00)",
                        }}
                     >
                        <AccordionSummary
                           id={`accordion-header-${course.ID}`}
                           className={expanded === course.ID ? styles.activeCourseHeader : ''}
                           expandIcon={
                              accelerator.collapsable_accordions
                                 ? openAccordions.includes(course.ID)
                                    ? <ExpandLessIcon />
                                    : <ExpandMoreIcon />
                                 : null
                           }
                           sx={{
                              "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                                 transform: "rotate(0deg)",
                              },
                                backgroundColor: (accelerator.custom_modules && moduleIndex === 0) ? "#FCFAFA" : null
                           }}
                        >
                           {/* checkmark for module */}
                           {(allLessonsComplete || coursesCompleted[course.ID] === course.ID) && !accordionFirstTimeComplete && (
                           <Box
                           component={"img"}
                           sx={{
                               width: { xs: "18px", md: "26px" },
                               height: { xs: "18px", md: "26px" },
                               margin: "auto 15px auto 0"
                           }}
                           src="https://members.performancegolf.com/wp-content/uploads/2025/02/Icon-1.svg"
                           alt=""/>
                           )}
                           {accordionFirstTimeComplete && (
                           <Box
                           sx={{
                              position: "relative",
                              width: { xs: "24px", md: "35px" },
                              height: { xs: "24px", md: "35px" },
                              margin: "auto 12px auto 0",
                              aspectRatio: "1 / 1",
                              display: "inline-block"
                           }}
                           >
                              <CheckAnimation/>
                           </Box>
                           )}
                           <Box
                           sx={{
                              display:  "inline-block",
                           }}>
                              <Typography
                                 fontSize={"16px"}
                                 margin={0}
                                 fontWeight={900}
                                 className={styles.lessonTitle}
                              >
                                 {parse(course.name)}
                              </Typography>
                              {course.lessons.length > 0 && (
                                 <Typography fontSize={16} fontWeight={400} sx={{ marginTop: {xs: "-7px", md: "0px"} }}>
                                    {" "}
                                    (
                                    {
                                       course.lessons.filter(
                                          (el) => el.lesson_type === 'text' || el.lesson_type === 'link' || el.lesson_type === 'video'
                                       ).length
                                    }
                                    {course.lessons.filter(
                                       (el) => el.lesson_type === 'text' || el.lesson_type === 'link'
                                    ).length > 0
                                       ? 
                                       course.lessons.filter(
                                          (el) => el.lesson_type === 'text' || el.lesson_type === 'link' || el.lesson_type === 'video'
                                       ).length > 1 ? " items" : " item"
                                       : 
                                       course.lessons.filter(
                                          (el) => el.lesson_type === 'video'
                                       ).length > 1 ? ` ${window.pg?.l10n.videos}` : ` ${window.pg?.l10n.video}`}
                                    )
                                 </Typography>
                              )}
                           </Box>
                        </AccordionSummary>
                     </Box>
                     <AccordionDetails>
                        {lessonPlaylist.map((lesson, lessonIndex,arr) =>
                           (
                              <>{renderPlaylistItem(lesson, lessonIndex,arr,moduleIndex)}</>
                           )
                        )}
                     </AccordionDetails>
                  </Accordion>
                  }
               </Box>
            )}
         </Box>
      </>
   );
};

export default CoursePlaylist;
